import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGetData,
  type GetImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGetError,
  type GetImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGetResponse,
  getImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGet,
} from '../../generated';

export type GetImageScoresQueryVariables = GetImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGetData['path'];

export type GetImageScoresQuery = GetImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGetResponse;

export const useGetImageScoresQuery = <
  TData = GetImageScoresQuery,
  TError = GetImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGetError,
>(
  { workspace_id, pdp_id }: GetImageScoresQueryVariables,
  options?: Omit<UseQueryOptions<GetImageScoresQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageScoresQuery, TError, TData>({
    queryKey: ['GetImageScores', { workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageScoresQuery;
    },
    ...options,
  });
};

useGetImageScoresQuery.getKey = ({ workspace_id, pdp_id }: GetImageScoresQueryVariables) => [
  'GetImageScores',
  { workspace_id, pdp_id },
];

useGetImageScoresQuery.fetcher = (
  { workspace_id, pdp_id }: GetImageScoresQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageScoresV1WorkspacesWorkspaceIdPdpsPdpIdImageScoresGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageScoresQuery;
  };
};

useGetImageScoresQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/image-scores',
};
